/**
 * Os modelos de treino do profissional.
 *
 * A lista **não** carrega os itens — o servidor não os manda aqui. Quarenta
 * modelos de doze itens trariam quinhentas linhas para desenhar uma lista de
 * títulos, e os itens chegam ao abrir um.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import type { Template, TemplateStatus } from '@/features/templates/api';
import { typeForModality } from '@/features/templates/prescriptions';
import {
  templateErrorKey,
  useCreateTemplate,
  useDuplicateTemplate,
  useSetTemplateStatus,
  useTemplates,
} from '@/features/templates/useTemplates';
import { LEVELS, MODALITIES } from '@/features/curation/vocabulary';

const GOALS = [
  'general_health',
  'weight_loss',
  'muscle_gain',
  'endurance',
  'mobility',
  'performance',
] as const;

export default function TemplatesPage() {
  const { t } = useTranslation(['templates', 'common']);
  const [status, setStatus] = useState<TemplateStatus>('active');
  const lista = useTemplates(status);

  return (
    <div className="px-5 py-6">
      <h1 className="text-3xl">{t('templates:title')}</h1>
      <p className="mt-1 text-(--color-sand-700)">{t('templates:subtitle')}</p>

      <NovoModelo />

      <div className="mt-5 flex gap-2">
        {(['active', 'archived'] as const).map((opcao) => (
          <button
            key={opcao}
            type="button"
            aria-pressed={status === opcao}
            onClick={() => setStatus(opcao)}
            className={[
              'rounded-full border px-4 py-2 text-sm font-medium transition-colors',
              status === opcao
                ? 'border-(--color-amber-500) bg-(--color-amber-50) text-(--color-sand-900)'
                : 'border-(--color-sand-200) bg-(--color-sand-0) text-(--color-sand-700)',
            ].join(' ')}
          >
            {t(`templates:status.${opcao}`)}
          </button>
        ))}
      </div>

      {lista.isError ? (
        <div className="mt-5">
          <Alert tone="error" live="assertive">
            {t(templateErrorKey(lista.error))}
          </Alert>
        </div>
      ) : null}

      {lista.isPending ? (
        <p className="mt-6 text-(--color-sand-700)" aria-busy="true">
          {t('common:state.loading')}
        </p>
      ) : null}

      {lista.isSuccess ? (
        lista.data.items.length === 0 ? (
          <p className="mt-6 text-(--color-sand-700)">{t('templates:empty')}</p>
        ) : (
          <ul className="mt-4 flex flex-col gap-3">
            {lista.data.items.map((modelo) => (
              <li key={modelo.template_id}>
                <TemplateCard modelo={modelo} />
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}

function TemplateCard({ modelo }: { modelo: Template }) {
  const { t } = useTranslation(['templates', 'curation', 'common']);
  const arquivar = useSetTemplateStatus();
  const duplicar = useDuplicateTemplate();
  const navigate = useNavigate();

  const arquivado = modelo.status === 'archived';

  return (
    <div className="rounded-2xl border border-(--color-sand-200) bg-(--color-sand-0) p-4">
      <Link to={`/templates/${modelo.template_id}`} className="block">
        <span className="font-semibold text-(--color-sand-900)">{modelo.title}</span>
        <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-(--color-sand-600)">
          <span>{t(`curation:modality.${modelo.modality}`)}</span>
          <span aria-hidden="true">·</span>
          <span>{t(`curation:level.${modelo.level}`)}</span>
          {modelo.estimated_minutes ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{t('templates:unit.minutes', { count: modelo.estimated_minutes })}</span>
            </>
          ) : null}
        </span>
      </Link>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={duplicar.isPending}
          onClick={() =>
            duplicar.mutate(
              {
                templateId: modelo.template_id,
                title: t('templates:copyOf', { title: modelo.title }),
              },
              { onSuccess: (novo) => navigate(`/templates/${novo.template_id}`) },
            )
          }
          className="mh-button mh-button--ghost text-sm"
        >
          {t('templates:action.duplicate')}
        </button>
        <button
          type="button"
          disabled={arquivar.isPending}
          onClick={() =>
            arquivar.mutate({
              templateId: modelo.template_id,
              status: arquivado ? 'active' : 'archived',
              version: modelo.version,
            })
          }
          className="mh-button mh-button--ghost text-sm"
        >
          {arquivado ? t('templates:action.restore') : t('templates:action.archive')}
        </button>
      </div>
    </div>
  );
}

/**
 * Criar pede só o essencial; os itens entram no editor.
 *
 * Começa fechado pelo mesmo motivo da curadoria: a tela existe para abrir o que
 * já existe, e um formulário permanente no topo empurraria a lista para baixo
 * em toda visita.
 */
function NovoModelo() {
  const { t } = useTranslation(['templates', 'curation', 'common']);
  const navigate = useNavigate();
  const criar = useCreateTemplate();

  const [aberto, setAberto] = useState(false);
  const [title, setTitle] = useState('');
  const [modality, setModality] = useState<string>('strength');
  const [level, setLevel] = useState<string>('beginner');
  const [objective, setObjective] = useState<string>('');

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="mh-button mh-button--secondary mt-4"
      >
        {t('templates:create.open')}
      </button>
    );
  }

  return (
    <section className="mt-4 rounded-2xl border border-(--color-sand-200) p-4">
      <h2 className="text-lg font-semibold">{t('templates:create.title')}</h2>

      <label className="mt-3 block">
        <span className="text-sm font-medium text-(--color-sand-800)">
          {t('templates:field.title')}
        </span>
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={160}
          autoFocus
          className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
        />
      </label>

      <Escolha
        label={t('templates:field.modality')}
        value={modality}
        onChange={setModality}
        options={MODALITIES}
        prefix="curation:modality"
      />
      <Escolha
        label={t('templates:field.level')}
        value={level}
        onChange={setLevel}
        options={LEVELS}
        prefix="curation:level"
      />
      <Escolha
        label={t('templates:field.objective')}
        value={objective}
        onChange={setObjective}
        options={GOALS}
        prefix="templates:goal"
        vazio={t('templates:field.noObjective')}
      />

      {criar.isError ? (
        <div className="mt-3">
          <Alert tone="error" live="assertive">
            {t(templateErrorKey(criar.error))}
          </Alert>
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={title.trim() === '' || criar.isPending}
          onClick={() =>
            criar.mutate(
              {
                title: title.trim(),
                modality,
                level,
                objective: objective || null,
                estimated_minutes: null,
                // Nasce sem itens: eles entram no editor, que abre em seguida.
                items: [],
              },
              { onSuccess: (novo) => navigate(`/templates/${novo.template_id}`) },
            )
          }
          className="mh-button mh-button--primary"
        >
          {t('templates:create.submit')}
        </button>
        <button
          type="button"
          onClick={() => setAberto(false)}
          className="mh-button mh-button--ghost"
        >
          {t('common:action.cancel')}
        </button>
      </div>

      <p className="mt-3 text-xs text-(--color-sand-600)">
        {t('templates:create.hint', {
          type: t(`templates:prescription.${typeForModality(modality)}`),
        })}
      </p>
    </section>
  );
}

export function Escolha({
  label,
  value,
  onChange,
  options,
  prefix,
  vazio,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<string>;
  prefix: string;
  vazio?: string;
}) {
  const { t } = useTranslation(['templates', 'curation']);

  return (
    <label className="mt-3 block">
      <span className="text-sm font-medium text-(--color-sand-800)">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-3"
      >
        {vazio ? <option value="">{vazio}</option> : null}
        {options.map((opcao) => (
          <option key={opcao} value={opcao}>
            {t(`${prefix}.${opcao}`)}
          </option>
        ))}
      </select>
    </label>
  );
}
